import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MyBookingsResponse } from '@ah/contracts';
import { useI18n, LanguageSwitcher } from '@ah/i18n';
import { useAuthStore } from '../features/auth/auth.store';
import { useBookingsApi } from '../features/bookings/useBookingsApi';
import { BookingsApiError } from '../features/bookings/bookings.api';
import { usePaymentsApi } from '../features/payments/usePaymentsApi';
import { PaymentsApiError } from '../features/payments/payments.api';

type LoadState = 'loading' | 'loaded' | 'error';

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--ah-color-border, #e2e2e2)',
  borderRadius: 'var(--ah-radius-md, 8px)',
  padding: 'var(--ah-space-3)',
  marginBottom: 'var(--ah-space-2)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--ah-color-text-muted)',
};

const actionButtonStyle = (variant: 'accept' | 'reject'): React.CSSProperties => ({
  padding: 'var(--ah-space-1) var(--ah-space-2)',
  border: '1px solid var(--ah-color-border, #e2e2e2)',
  borderRadius: 'var(--ah-radius-md, 6px)',
  background: variant === 'accept' ? '#2e7d32' : '#c62828',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  marginRight: 6,
});

/**
 * Therapist Portal dashboard (Module 9) — GET /api/v1/bookings/my.
 * Reached only when signed in — <RequireAuth> in App.tsx redirects to
 * /login otherwise, so the access token is always present here.
 */
export default function DashboardPage() {
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  const displayName = useAuthStore((s) => s.user?.displayName);
  const therapistStatus = useAuthStore((s) => s.user?.therapist?.status);
  const signOut = useAuthStore((s) => s.signOut);
  const bookingsApi = useBookingsApi();
  const paymentsApi = usePaymentsApi();
  const { t } = useI18n();
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<MyBookingsResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!accessToken) return;
    setState('loading');
    bookingsApi
      .listMine(accessToken)
      .then((result) => {
        setData(result);
        setState('loaded');
      })
      .catch((err) => {
        setErrorMessage(err instanceof BookingsApiError ? err.message : t('dashboard.loadError'));
        setState('error');
      });
  }, [accessToken, bookingsApi, t]);

  useEffect(() => {
    load();
  }, [load]);

  const accept = async (bookingId: string) => {
    if (!accessToken) return;
    setActioningId(bookingId);
    try {
      await paymentsApi.acceptBooking(accessToken, bookingId);
      load();
    } catch (err) {
      setErrorMessage(err instanceof PaymentsApiError ? err.message : t('dashboard.acceptError'));
    } finally {
      setActioningId(null);
    }
  };

  const reject = async (bookingId: string) => {
    if (!accessToken) return;
    const reason = window.prompt(t('dashboard.declinePrompt')) ?? undefined;
    setActioningId(bookingId);
    try {
      await paymentsApi.rejectBooking(accessToken, bookingId, reason);
      load();
    } catch (err) {
      setErrorMessage(err instanceof PaymentsApiError ? err.message : t('dashboard.declineError'));
    } finally {
      setActioningId(null);
    }
  };

  return (
    <main style={{ padding: 'var(--ah-space-4)', maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1 style={{ color: 'var(--ah-color-primary)' }}>{t('dashboard.title')}{displayName ? ` — ${displayName}` : ''}</h1>
        <div style={{ display: 'flex', gap: 'var(--ah-space-3)', alignItems: 'baseline' }}>
          <LanguageSwitcher />
          <Link to="/profile" style={{ color: 'var(--ah-color-primary)' }}>
            {t('dashboard.myProfile')}
          </Link>
          <Link to="/payouts" style={{ color: 'var(--ah-color-primary)' }}>
            {t('dashboard.payouts')}
          </Link>
          <Link to="/settings" style={{ color: 'var(--ah-color-primary)' }}>
            {t('dashboard.settings')}
          </Link>
          <button onClick={signOut} style={{ background: 'none', border: 'none', color: 'var(--ah-color-text-muted)', cursor: 'pointer' }}>
            {t('dashboard.signOut')}
          </button>
        </div>
      </div>

      {therapistStatus && therapistStatus !== 'APPROVED' && (
        <p
          role="status"
          style={{
            background: 'var(--ah-color-warning-bg, #fff8e1)',
            border: '1px solid var(--ah-color-warning, #f0c419)',
            borderRadius: 'var(--ah-radius-md, 8px)',
            padding: 'var(--ah-space-3)',
            fontSize: 14,
          }}
        >
          {therapistStatus === 'PENDING_REVIEW' && t('dashboard.pendingReview')}
          {therapistStatus === 'SUSPENDED' && t('dashboard.suspended')}
          {therapistStatus === 'REJECTED' && t('dashboard.rejected')}
        </p>
      )}

      {state === 'loading' && <p role="status">{t('dashboard.loading')}</p>}

      {state === 'error' && <p role="alert">{errorMessage}</p>}

      {state === 'loaded' && data && (
        <section>
          <h2 style={{ fontSize: 18 }}>{t('dashboard.yourBookings')}</h2>
          {data.bookings.length === 0 && <p style={labelStyle}>{t('dashboard.noBookings')}</p>}
          {data.bookings.map((b) => (
            <div key={b.id} style={cardStyle}>
              <div>{new Date(b.scheduledAt).toLocaleString()}</div>
              <div style={labelStyle}>
                {b.status} · {b.callMode} · {b.durationMinutes} min
                {b.serviceName ? ` · ${b.serviceName}` : ''}
              </div>

              {b.status === 'PENDING' && b.paymentStatus === 'AUTHORIZED' && (
                <div style={{ marginTop: 'var(--ah-space-2)' }}>
                  {actioningId === b.id ? (
                    <span style={labelStyle}>{t('dashboard.working')}</span>
                  ) : (
                    <>
                      <button style={actionButtonStyle('accept')} onClick={() => accept(b.id)}>
                        {t('dashboard.accept')}
                      </button>
                      <button style={actionButtonStyle('reject')} onClick={() => reject(b.id)}>
                        {t('dashboard.decline')}
                      </button>
                    </>
                  )}
                </div>
              )}

              {b.status === 'PENDING' && b.paymentStatus === 'PENDING' && (
                <p style={{ ...labelStyle, marginTop: 'var(--ah-space-2)' }}>{t('dashboard.awaitingPayment')}</p>
              )}

              {b.status === 'CONFIRMED' && <Link to={`/call/${b.id}`}>{t('dashboard.openCall')}</Link>}
            </div>
          ))}
        </section>
      )}
    </main>
  );
}
